// Hub sign-in.
//
// The brand is white-dominant (brand.html §03), so this is a light screen:
// the form sits on a paper surface inside a white canvas, the primary action is
// a black fill, and errors are written as sentences that say how to fix the
// problem (§05, §06). The old dark treatment belonged to the previous identity.
import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Logo } from "@/components/Logo";
import { type SessionUser, type TwoStepChallenge, useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";

export function LoginPage() {
  const { login, adoptSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Two-step sign-in (2026-10-21): the 6-digit code after the password.
  const [challenge, setChallenge] = useState<TwoStepChallenge | null>(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);

  const goOn = () => {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    navigate(from, { replace: true });
  };

  const handleCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      const body = await api<{ access_token: string; user: SessionUser }>("/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ code: code.trim(), remember_device: remember }),
        headers: { Authorization: `Bearer ${challenge.token}` },
        skipAuth: true,
        skipAuthRefresh: true,
      });
      adoptSession(body.access_token, body.user);
      goOn();
    } catch (err) {
      setCode("");
      if (err instanceof ApiError && err.status === 400) {
        setError("That code didn't work. Check the app and try again — the code changes every 30 seconds.");
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Too many wrong codes. Wait 15 minutes and try again.");
      } else if (err instanceof ApiError && err.status === 401) {
        setError("That took too long. Please sign in again.");
        setChallenge(null);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await login(email.trim(), password);
      if (outcome.kind === "two_step") {
        setPassword("");
        setChallenge(outcome.challenge);
        return;
      }
      goOn();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Email or password didn't match.");
        } else if (err.status === 429) {
          const retryAt = (err.body as { retry_at?: string } | null)?.retry_at;
          setError(
            retryAt
              ? `Too many attempts. Try again at ${new Date(retryAt).toLocaleTimeString()}.`
              : "Too many failed attempts. Please wait 30 minutes.",
          );
        } else if (err.status === 403) {
          setError("Your password needs to be reset.");
        } else if (err.status === 503 && (err.body as { error?: string } | null)?.error === "mfa_misconfigured") {
          setError("Two-step sign-in is misconfigured — contact admin.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else {
        setError("Network error. Check your connection.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6">
      <div className="w-full max-w-[380px]">
        <div className="mb-10 flex justify-center">
          <Logo />
        </div>

        <div className="surface-paper p-8">
          <h1 className="text-section font-bold">Hub</h1>
          <p className="mt-1 text-label text-ink-muted">
            Everything that runs the business.
          </p>

          {challenge?.setup ? (
            <div className="mt-8 space-y-5">
              <p className="text-body">
                Set up two-step sign-in in Flow first: sign in at{" "}
                <a className="underline" href="https://flow.withother.com/login">flow.withother.com</a>, then
                come back here.
              </p>
              <button type="button" className="btn-primary w-full" onClick={() => setChallenge(null)}>
                Back
              </button>
            </div>
          ) : challenge ? (
            <form onSubmit={handleCode} className="mt-8 space-y-5">
              <div>
                <label htmlFor="code" className="mb-1.5 block text-label font-semibold">
                  Code from your authenticator app
                </label>
                <Input
                  id="code"
                  autoFocus
                  autoComplete="one-time-code"
                  required
                  maxLength={20}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                />
                <p className="mt-1.5 text-meta text-ink-muted">Lost your phone? Type a recovery code instead.</p>
              </div>
              <label className="flex items-center gap-2 text-label">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                Remember this device for 30 days
              </label>
              {error && (
                <div role="alert" className="border border-risk/30 bg-risk-bg px-3 py-2 text-label text-risk">
                  {error}
                </div>
              )}
              <button type="submit" className="btn-primary w-full" disabled={submitting}>
                {submitting ? "Checking…" : "Sign in"}
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-label font-semibold"
              >
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@withother.com"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-label font-semibold"
              >
                Password
              </label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <div
                role="alert"
                className="border border-risk/30 bg-risk-bg px-3 py-2 text-label text-risk"
              >
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
          )}
        </div>

        <p className="mt-6 text-center text-meta text-ink-muted">
          © {new Date().getFullYear()} other · Boca Raton, FL
        </p>
      </div>
    </div>
  );
}
